import React from 'react'
import { InfoCircleOutlined, CheckCircleOutlined } from '@ant-design/icons'

interface AttributeData {
  type?: string
  operator?: string
  value?: any
  time_operator?: string
  time_value?: string
  count?: number
}

interface AttributeTooltipProps {
  data: AttributeData
  dimensions: any[]
}

const operatorMapping: { [key: string]: string } = {
  EQUAL: 'equals',
  NOT_EQUAL: 'not equals',
  GREATER_THAN: 'greater than',
  LESS_THAN: 'less than',
  GREATER_THAN_OR_EQUAL: '≥',
  LESS_THAN_OR_EQUAL: '≤',
  IS_BETWEEN: 'between',
  IS_NOT_BETWEEN: 'not between',
  CONTAINS: 'contains',
  DOES_NOT_CONTAIN: 'does not contain',
  STARTS_WITH: 'starts with',
  DOES_NOT_START_WITH: 'does not start with',
  ENDS_WITH: 'ends with',
  DOES_NOT_END_WITH: 'does not end with',
  IN_THE_FOLLOWING: 'in',
  NOT_IN_THE_FOLLOWING: 'not in',
}

export const AttributeTooltip: React.FC<AttributeTooltipProps> = ({ data, dimensions }) => {
  const getSegmentType = (type?: string) => {
    return dimensions?.find((item) => item.code === type)
  }

  const getOperatorName = (type?: string, operator?: string) => {
    const segmentType = getSegmentType(type)
    const opera = segmentType?.operator?.find((item: any) => item?.code === operator)
    return opera?.name || operatorMapping[operator || ''] || operator || ''
  }

  const getSegmentValue = (type?: string, dimValue?: any) => {
    if (typeof dimValue === 'number') return Number(dimValue)?.toLocaleString()
    if (typeof dimValue === 'string' && !dimValue.startsWith('PRODUCT_')) return dimValue

    const segmentType = getSegmentType(type)
    const value =
      segmentType?.values?.find((item: any) => item?.code === dimValue) ||
      segmentType?.value?.find((item: any) => item?.code === dimValue)

    return value?.name || dimValue
  }

  const formatValue = () => {
    let value = data.value
    
    // Handle product IDs and category IDs
    if (typeof value === 'string') {
      if (value.includes('PRODUCT_')) {
        const products = value.split(',').map(p => p.trim())
        if (products.length > 2) {
          return `${products.slice(0, 2).join(', ')}... (${products.length} items)`
        }
        return products.join(', ')
      }
      if (value.includes('CATEGORY_')) {
        const categories = value.split(',').map(c => {
          const parts = c.trim().split('_')
          return parts.slice(2).join(' ')
        })
        if (categories.length > 2) {
          return `${categories.slice(0, 2).join(', ')}... (${categories.length} categories)`
        }
        return categories.join(', ')
      }
    }
    
    return getSegmentValue(data.type, value)
  }

  const segmentType = getSegmentType(data.type)
  const operatorName = getOperatorName(data.type, data.operator)
  const formattedValue = formatValue()

  return (
    <div className="attribute-tooltip">
      <div className="tooltip-header">
        <InfoCircleOutlined className="mr-2" />
        <span className="font-semibold">Attribute Details</span>
      </div>
      
      <div className="tooltip-content">
        <div className="attribute-section">
          <div className="attribute-label">Type:</div>
          <div className="attribute-value">{segmentType?.name || data.type}</div>
        </div>
        
        {operatorName && (
          <div className="attribute-section">
            <div className="attribute-label">Operator:</div>
            <div className="attribute-operator">{operatorName}</div>
          </div>
        )}
        
        {formattedValue && (
          <div className="attribute-section">
            <div className="attribute-label">Value:</div>
            <div className="attribute-value-text">{formattedValue}</div>
          </div>
        )}
        
        {(data.time_operator || data.time_value) && (
          <div className="attribute-section">
            <div className="attribute-label">Time:</div>
            <div className="attribute-value">
              {data.time_operator} {data.time_value}
            </div>
          </div>
        )}
        
        {data.count && (
          <div className="attribute-section">
            <div className="attribute-label">Count:</div>
            <div className="attribute-value">{data.count} times</div>
          </div>
        )}
      </div>
      
      <div className="tooltip-footer">
        <CheckCircleOutlined className="text-green-500 mr-1" />
        <span className="text-xs">Condition Active</span>
      </div>
    </div>
  )
}